import Decimal from 'decimal.js';
import { ValidationErrors } from 'final-form';
import { MessageDescriptor } from 'react-intl';

import { ProductHierarchyLevel, UserRequirement } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { localize } from 'common/utils';
import { ItemQuantity } from 'modules/menu/items/types';
import { OfferInventoryDataResponse, SelectionCategory } from 'modules/shared-menu/types';
import { getTypedKeys } from 'modules/shared-menu/utils';

import { OfferTab, advancedTabFields, availabilityTabFields, detailsTabFields, AllTabsFields } from './constants';
import {
  WOLT_SPECIALS_CATEGORY_ID,
  sharedMenuMandatoryWarningTranslationMap,
  sharedMenuMandatoryWarningsForCountry,
} from '../../constants';
import { SharedMenuDietaryPreference, SharedMenuMandatoryWarning } from '../../types';
import { createCategoryOption } from '../../utils';

export const getCurrencyMaxDecimals = (currency: Currency) => {
  return Intl.NumberFormat(undefined, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
};

export const getPercentageDiscountPrice = (basePrice: number, percentage: number, currency: Currency) => {
  const maxDecimals = getCurrencyMaxDecimals(currency);

  const basePriceDecimal = new Decimal(basePrice);
  const discountPercentageDecimal = new Decimal(percentage).div(100);

  const priceDiscountDecimal = basePriceDecimal.times(discountPercentageDecimal);
  const discountedPriceDecimal = basePriceDecimal.sub(priceDiscountDecimal);

  return discountedPriceDecimal.toFixed(maxDecimals).toString();
};

export const getDiscountPercentage = (discountedPrice: number, originalPrice: number) => {
  const discountedPriceDecimal = new Decimal(discountedPrice);
  const originalPriceDecimal = new Decimal(originalPrice);
  const division = discountedPriceDecimal.div(originalPriceDecimal);

  return new Decimal(1.0).sub(division).times(100).round().toNumber();
};

export const alcoholPercentageToPermilles = (percentage: number) => percentage * 10;

type TabsWithPossibleFieldError = Exclude<OfferTab, 'OPTIONS'>;

export const getTabsErrors = (errors: ValidationErrors = {}): Record<TabsWithPossibleFieldError, number> => ({
  DETAILS: getTypedKeys(errors).filter((e: AllTabsFields) => e in detailsTabFields).length,
  ADVANCED: getTypedKeys(errors).filter((e: AllTabsFields) => e in advancedTabFields).length,
  AVAILABILITY: getTypedKeys(errors).filter((e: AllTabsFields) => e in availabilityTabFields).length,
});

export const getCategoryOptions = ({
  categories,
  primaryLanguage,
}: {
  categories: SelectionCategory[];
  primaryLanguage: Language;
}) =>
  categories
    .filter(category => category.id !== WOLT_SPECIALS_CATEGORY_ID)
    .flatMap(category => [
      createCategoryOption(category, null, primaryLanguage),
      ...(category.subcategories ?? []).map(subcategory =>
        createCategoryOption(subcategory, category.id, primaryLanguage),
      ),
    ]);

export type ProductHierarchyOption = ReturnType<typeof getProductHierarchyOptions>[number];

export const getProductHierarchyOptions = (
  productHierarchyLevels: ProductHierarchyLevel[] | undefined,
  locale: string,
) =>
  (productHierarchyLevels ?? [])
    .map((level, _, array) => ({
      value: level.id,
      label: localize(level.name, locale),
      secondaryContent: level.human_readable_id,
      parentId: level.parent_category_id,
      childrenIds: array.filter(item => item.parent_category_id === level.id).map(item => item.id),
      tags: level.tags,
    }))
    .sort((a, b) => Number(a.secondaryContent) - Number(b.secondaryContent));

export const getUserRequirementOptions = (
  intlFormatter: (descriptor: MessageDescriptor) => string,
): { value: UserRequirement; label: string }[] => [
  { value: 'no_wolt_purchases', label: intlFormatter({ id: 'menu-item.ftu-restriction-values.ftu-wolt' }) },
  {
    value: 'no_franchise_purchases',
    label: intlFormatter({ id: 'menu-item.ftu-restriction-values.ftu-franchise' }),
  },
  {
    value: 'no_venue_purchases',
    label: intlFormatter({ id: 'menu-item.ftu-restriction-values.ftu-venue' }),
  },
];

export const getDietaryPreferenceOptions = (
  intlFormatter: (descriptor: MessageDescriptor) => string,
): { value: SharedMenuDietaryPreference; label: string }[] => [
  {
    value: 'gluten_free',
    label: intlFormatter({ id: 'menu.dietaryPreferences.glutenFree' }),
  },
  {
    value: 'lactose_free',
    label: intlFormatter({ id: 'menu.dietaryPreferences.lactoseFree' }),
  },
  {
    value: 'halal',
    label: intlFormatter({ id: 'menu.dietaryPreferences.halal' }),
  },
  {
    value: 'kosher',
    label: intlFormatter({ id: 'menu.dietaryPreferences.kosher' }),
  },
  {
    value: 'vegan',
    label: intlFormatter({ id: 'menu.dietaryPreferences.vegan' }),
  },
  {
    value: 'vegetarian',
    label: intlFormatter({ id: 'menu.dietaryPreferences.vegetarian' }),
  },
];

export const getMandatoryWarningOptions = (
  country: Country,
  intlFormatter: (descriptor: MessageDescriptor) => string,
): { value: SharedMenuMandatoryWarning; label: string }[] =>
  sharedMenuMandatoryWarningsForCountry[country]?.map((warningForCountry: SharedMenuMandatoryWarning) => ({
    value: warningForCountry,
    label: intlFormatter({ id: sharedMenuMandatoryWarningTranslationMap[warningForCountry] }),
  })) || [];

export const transformOfferInventoryToQuantity = (offerInventory: OfferInventoryDataResponse): ItemQuantity => {
  return {
    balance: offerInventory.total,
    reserved: offerInventory.total - offerInventory.available,
    available: offerInventory.available,
  };
};
